/**
 * @file loginView.jsx
 * @author Fernando Ruano
 * @description Login View Page
 */

// Dependencies
import React from 'react';
import Countdown, { zeroPad } from 'react-countdown';
import PropTypes from 'prop-types';
import {
  Button,
  Form,
  Input,
  Menu,
  Alert,
  Spin,
  Select,
  Space,
  Divider
} from 'antd';
import {
  MailFilled,
  MessageFilled,
  PhoneFilled,
  ArrowLeftOutlined,
  CloseCircleFilled,
  CheckCircleFilled
} from '@ant-design/icons';
import { useTranslation } from 'react-i18next';
import { useHistory, useLocation } from 'react-router-dom';
import styled from 'styled-components';
import { useOktaAuth } from '@okta/okta-react';

// Helpers
import {
  setStorageItem,
  getStorageItem
} from '@nephro/commons/helpers/storage-helper';
import { onKeepOnlyNumbers } from '@nephro/commons/helpers/string-helper';

// Assets
import baxterNephroLogo from '@nephro/assets/logoNefroPlus.png';
import baxterRenalHorizontal from '@nephro/assets/baxterRenalWhite.png';
import baxterRenalVertical from '@nephro/assets/baxterRenalVertical.png';

// Styles
import { customDelay } from 'commons/helpers/utils';
import { PatientService } from 'services/patient-service';
import { externalConnector } from 'core/connector';
import tokenInterceptor from 'core/axios/interceptors/tokenInterceptor';
import { UserService } from 'services/user-service';
import styles from './styles.module.css';

const StyledMainDiv = styled.div`
  .ant-select-single:not(.ant-select-customize-input) .ant-select-selector {
    height: 40px;
  }
  .ant-select-single:not(.ant-select-customize-input)
    .ant-select-selector
    .ant-select-selection-search-input {
    height: 38px;
  }

  .ant-select-single.ant-select-show-arrow .ant-select-selection-item,
  .ant-select-single.ant-select-show-arrow .ant-select-selection-placeholder {
    margin-top: 3px;
  }
`;

const { Option } = Select;
let uName = null;
function loginView({ authActions, userActions, patientActions, loading }) {
  const { t: translate } = useTranslation();
  const { pathname } = useLocation();
  const isStaffPath = pathname === '/loginStaff';
  const [currentTab] = React.useState(isStaffPath ? 'staff' : 'patient');
  const [renderContent, setRenderContent] = React.useState('auth');
  const [isCodeAccepted, setIsCodeAccepted] = React.useState(false);
  const [codeExpireInMilliseconds, setCodeExpireInMilliseconds] =
    React.useState(120000);
  const [codeExpireRequestTime, setCodeExpireRequestTime] = React.useState(
    Date.now()
  );
  const [currentFactorTab, setCurrentFactorTab] = React.useState('email');
  const [smsFactor, setSmsFactor] = React.useState({
    factorId: 0,
    name: 'sms',
    value: 'none'
  });
  const [codeInput, setCodeInput] = React.useState({
    digit01: '',
    digit02: '',
    digit03: '',
    digit04: '',
    digit05: '',
    digit06: ''
  });
  const digit01 = React.useRef('codeInput01');
  const digit02 = React.useRef('codeInput02');
  const digit03 = React.useRef('codeInput03');
  const digit04 = React.useRef('codeInput04');
  const digit05 = React.useRef('codeInput05');
  const digit06 = React.useRef('codeInput06');

  const [emailFactor, setEmailFactor] = React.useState({
    factorId: 0,
    name: 'email',
    value: 'none'
  });
  const [callFactor, setCallFactor] = React.useState({
    factorId: 0,
    name: 'call',
    value: 'none'
  });
  const [isError, setIsError] = React.useState(false);
  const [showFaNewCodeAction, setShowFaNewCodeAction] = React.useState(true);
  const [messageError, setMessageError] = React.useState(null);
  const [identificationTypesList, setIdentificationTypesList] =
    React.useState(null);
  const { logIn, sendCode, verifyCode, getToken } = authActions;
  const { setUserInfo, getUserDataRegister, getUserLoginInfo } = userActions;
  const { getIdentificationTypesArray } = patientActions;
  const history = useHistory();
  const isPatient = currentTab !== 'staff';
  const [userStateToken, setUserStateToken] = React.useState('');
  const [patientDocumentType, setPatientDocumentType] = React.useState('');
  const [loadingVerify, setLoadingVerify] = React.useState(false);

  // Okta
  const { oktaAuth } = useOktaAuth();
  const onLoginOkta = () => {
    // console.log(oktaAuth.getIssuerOrigin());
    oktaAuth.signInWithRedirect();
  };

  /* eslint-disable no-template-curly-in-string */
  const validateMessages = {
    required: translate('commons.required'),
    types: {
      email: translate('commons.wrongEmail'),
      number: '${label} is not a valid number!'
    },
    number: {
      range: '${label} must be between ${min} and ${max}'
    }
  };
  /* eslint-enable no-template-curly-in-string */

  // Function to get and set identification types list
  const onGetIdentificationTypesList = (_, response) => {
    if (response?.data?.content) {
      setIdentificationTypesList(response.data);
    }
  };

  setStorageItem('lastAuthPath', pathname);

  React.useEffect(() => {
    // Clear cache
    if ('caches' in window) {
      caches.keys().then((names) => {
        names.forEach((name) => {
          caches.delete(name);
        });
      });
    }

    if (getStorageItem('user')) {
      history.replace('/main');
    } else {
      getIdentificationTypesArray(
        { paged: false },
        onGetIdentificationTypesList
      );
    }
  }, []);

  const onNavigation = async () => {
    await customDelay(2500);
    history.replace('/main');
    window.location.reload();
  };

  // Get and set patient info after login (only patient users)
  const onGetPatientInfo = async (type, response) => {
    if (type === 'success') {
      await setStorageItem('patientInfo', response.data);
    }
  };

  // Get and set user info after login
  const onGetUserLoginInfo = async (type, response, userInfoPayload) => {
    if (type === 'success') {
      const patiensService = new PatientService(externalConnector);
      const userService = new UserService(externalConnector);

      if (isPatient) {
        externalConnector.baseConfig = tokenInterceptor();
        const userInfoData = await userService.getUserLoginInfo(
          userInfoPayload.username,
          isPatient,
          userInfoPayload.documentType
        );

        await setStorageItem('user', userInfoData.data);
        if (userInfoData.http_status === 200) {
          const data = await patiensService.getPatientById(response?.data?.id);
          await onGetPatientInfo('success', data);
        }
      }
      setUserInfo(response.data, onNavigation);
    }
  };

  const onSuccessLogin = (status, userInfo, userDataPayload) => {
    return setTimeout(async () => {
      await onGetUserLoginInfo(status, userInfo, userDataPayload);
      setStorageItem('isPatient', isPatient);
      setIsError(false);
      setRenderContent('auth');
    }, 1500);
  };

  function setFactors(keyValueFactors) {
    const smsData = keyValueFactors.filter((str) => str.includes('sms:'));
    const callData = keyValueFactors.filter((str) => str.includes('call:'));
    const emailData = keyValueFactors.filter((str) => str.includes('email:'));

    if (smsData.length > 0) {
      const valueData = smsData[0].split(':')[1].split(',');
      setSmsFactor((prevState) => ({
        ...prevState,
        id: valueData[0],
        value: valueData[1]
      }));
    }
    if (emailData.length > 0) {
      const valueData = emailData[0].split(':')[1].split(',');
      setEmailFactor((prevState) => ({
        ...prevState,
        id: valueData[0],
        value: valueData[1]
      }));
    }
    if (callData.length > 0) {
      const valueData = callData[0].split(':')[1].split(',');
      setCallFactor((prevState) => ({
        ...prevState,
        id: valueData[0],
        value: valueData[1]
      }));
    }
  }

  // Call user info services when logged
  const onRequestToken = (type, response) => {
    if (type === 'success') {
      const { accessToken } = response.data;
      setStorageItem('token', accessToken);
    } else {
      setStorageItem('token', 'none)');
    }
    setIsError(false);
  };

  // Call user info services when logged
  const onLogin = async (type, response, documentType) => {
    if (type === 'success') {
      const isStaff = currentTab === 'staff';
      const { stateToken, keyValueFactors, sessionToken } =
        response.data.userinfo;

      if (!isStaff && stateToken) {
        setUserStateToken(stateToken);
        setFactors(keyValueFactors);
        setRenderContent('mfa');
      } else if (sessionToken) {
        const data = {
          sessionToken,
          identityProvider: 'OKTA_PROVIDER'
        };

        await getToken(data, async (resultType, responseData) => {
          await onRequestToken(resultType, responseData);
        });

        const userDataPayload = {
          username: uName,
          isPatient,
          documentType
        };

        await getUserLoginInfo(userDataPayload, (status, userInfo) =>
          onSuccessLogin(status, userInfo, userDataPayload)
        );
      }
      setIsError(false);
    } else {
      setMessageError(
        response.code !== undefined
          ? `errors.${response.code}`
          : 'commons.problem'
      );
      setIsError(true);
    }
  };

  // Call user info services when logged
  const onVerify = async (type, response, documentType) => {
    if (type === 'success') {
      setIsCodeAccepted(true);
      const { sessionToken } = response.data;

      const data = {
        sessionToken,
        identityProvider: 'OKTA_PROVIDER'
      };

      await getToken(data, async (resultType, responseData) => {
        await onRequestToken(resultType, responseData);
      });

      const userDataPayload = {
        username: uName,
        isPatient,
        documentType
      };

      await getUserDataRegister(userDataPayload, (status, userInfo) =>
        onSuccessLogin(status, userInfo, userDataPayload)
      );
    } else {
      setMessageError(
        response.code !== undefined
          ? `errors.${response.code}`
          : 'commons.problem'
      );
      setIsError(true);
      setLoadingVerify(false);
    }
  };

  // After press login button when form full filled
  const onFinish = (values) => {
    setIsError(false);
    const isStaff = currentTab === 'staff';
    const { credentialsStaff, credentialsPatient, userName, docId } = values;
    const isLocal = window.location.hostname === 'localhost';
    const provider = isLocal ? 'OKTA_PROVIDER' : 'LDAP_PROVIDER';

    const data = {
      credentials: isStaff ? credentialsStaff : credentialsPatient,
      userName: !isStaff ? docId : userName,
      isEmail: isStaff ? '1' : '0',
      identityProvider: isStaff ? provider : 'OKTA_PROVIDER',
      documentType: isStaff ? '' : values.docType
    };
    setPatientDocumentType(values.docType);
    uName = !isStaff ? docId : userName;
    logIn(data, (type, response) =>
      onLogin(type, response, isStaff ? 1 : values.docType)
    );
  };

  function getSelectedFactor() {
    switch (currentFactorTab) {
      case 'sms':
        return smsFactor;
      case 'call':
        return callFactor;
      default:
        return emailFactor;
    }
  }

  // After press login button when form full filled
  const onFinishVerify = () => {
    setLoadingVerify(true);
    setIsError(false);

    const factorSelected = getSelectedFactor();
    const isStaff = currentTab === 'staff';
    const codeInputValues =
      codeInput.digit01 +
      codeInput.digit02 +
      codeInput.digit03 +
      codeInput.digit04 +
      codeInput.digit05 +
      codeInput.digit06;

    const data = {
      username: '',
      passcode: codeInputValues,
      stateToken: userStateToken,
      factorId: factorSelected.id,
      identityProvider: 'OKTA_PROVIDER'
    };
    verifyCode(data, (type, response) =>
      onVerify(type, response, isStaff ? 1 : patientDocumentType)
    );
  };

  // After press login button whit errors in form
  const onFinishFailed = () => setIsError(false);

  // After press login button whit errors in form
  const onUpdateInfo = () => setRenderContent('admin');

  // Set current tab (Staff or Patient tab)
  // const onChangeTab = (e) => {
  //   setIsError(false);
  //   setCurrentTab(e.key);
  // };

  // Set current factor tab (SMS or EMAIL tab)
  const onChangeTabFactor = (e) => {
    setCurrentFactorTab(e.key);
  };

  const onNavigationBack = () => {
    setIsError(false);
    if (renderContent === 'verify') {
      setRenderContent('mfa');
    } else if (renderContent === 'mfa' || renderContent === 'admin') {
      setRenderContent('auth');
    }
  };

  // After press login button when form full filled
  const onFinishMfa = () => {
    setIsError(false);
    const factorSelected = getSelectedFactor();

    const data = {
      stateToken: userStateToken,
      factorId: factorSelected.id,
      identityProvider: 'OKTA_PROVIDER'
    };
    sendCode(data, (type, response) => {
      if (type === 'success') {
        setIsCodeAccepted(false);
        setCodeExpireRequestTime(Date.now());
        const value = response.data?.expireTimeInMilliseconds;

        if (value) {
          setCodeExpireInMilliseconds(parseInt(value, 10));
        } else {
          setCodeExpireInMilliseconds(300000);
        }
        if (renderContent !== 'verify') {
          setRenderContent('verify');
        }
      } else {
        setMessageError(
          response.code !== undefined
            ? `errors.${response.code}`
            : 'commons.problem'
        );
        setIsError(true);
      }
    });
  };

  const onCodeInputChange = (input, nextInput, prevInput) => {
    setCodeInput((prevState) => ({
      ...prevState,
      [input.id]: input.value
    }));
    if (input.value.length > 0) {
      nextInput.current.focus();
    } else if (prevInput) {
      prevInput.current.focus();
    }
    setIsError(false);
  };

  // Form items by tab
  const inputGroupSpaced = () => (
    <Space
      align="center"
      justify="center"
      direction="horizontal"
      style={{ width: '100%', padding: '15px 10px', paddingBottom: '30px' }}>
      <Input
        id="digit01"
        className={styles.inputCode}
        style={{ padding: 0, minWidth: '80%' }}
        maxLength={1}
        ref={digit01}
        autoComplete="off"
        type="number"
        status="error"
        onChange={(e) => onCodeInputChange(e.target, digit02)}
      />
      <Input
        id="digit02"
        className={styles.inputCode}
        style={{ padding: 0, minWidth: '80%' }}
        maxLength={1}
        ref={digit02}
        autoComplete="off"
        type="number"
        status="error"
        onChange={(e) => onCodeInputChange(e.target, digit03, digit01)}
      />
      <Input
        id="digit03"
        className={styles.inputCode}
        style={{ padding: 0, minWidth: '80%' }}
        maxLength={1}
        ref={digit03}
        autoComplete="off"
        type="number"
        onChange={(e) => onCodeInputChange(e.target, digit04, digit02)}
      />
      <Input
        id="digit04"
        className={styles.inputCode}
        style={{ padding: 0, minWidth: '80%' }}
        maxLength={1}
        ref={digit04}
        autoComplete="off"
        type="number"
        status="error"
        onChange={(e) => onCodeInputChange(e.target, digit05, digit03)}
      />
      <Input
        id="digit05"
        className={styles.inputCode}
        style={{ padding: 0, minWidth: '80%' }}
        maxLength={1}
        ref={digit05}
        autoComplete="off"
        type="number"
        status="error"
        onChange={(e) => onCodeInputChange(e.target, digit06, digit04)}
      />
      <Input
        id="digit06"
        className={styles.inputCode}
        style={{ padding: 0, minWidth: '80%' }}
        maxLength={1}
        ref={digit06}
        autoComplete="off"
        type="number"
        status="error"
        onChange={(e) => onCodeInputChange(e.target, digit06, digit05)}
      />
      {isCodeAccepted && (
        <CheckCircleFilled className={styles.iconCheckedCode} />
      )}
      {isError && <CloseCircleFilled className={styles.iconErrorCode} />}
    </Space>
  );

  // Form items by tab
  const onChangeTabForm = ({
    labelUsername,
    itemNameUsername,
    itemNamePassword,
    normalize = null,
    type = null,
    usernamePlaceholder = ''
  }) => (
    <StyledMainDiv>
      {isPatient && (
        <Form.Item
          label={translate('patientMg.docType')}
          rules={[{ required: true, message: 'El tipo es requerido' }]}
          className={styles.mBottom}
          name={['docType']}>
          <Select
            placeholder={translate('patientMg.type')}
            optionFilterProp="children"
            filterOption={(input, option) =>
              option.children.toLowerCase().indexOf(input.toLowerCase()) >= 0
            }>
            {identificationTypesList?.content?.map((item, index) => (
              <Option key={index} value={item.identification_type_id}>
                {item.name}
              </Option>
            ))}
          </Select>
        </Form.Item>
      )}
      <Form.Item
        label={translate(labelUsername)}
        name={itemNameUsername}
        className="w-full"
        normalize={normalize}
        rules={[
          {
            type,
            required: true
          }
        ]}>
        <Input className="h-10" placeholder={usernamePlaceholder} />
      </Form.Item>
      <Form.Item
        label={translate('login.password')}
        name={itemNamePassword}
        rules={[
          {
            required: true
          }
        ]}>
        <Input.Password
          className="h-10"
          placeholder={translate('login.password')}
        />
      </Form.Item>
    </StyledMainDiv>
  );

  const authView = () => (
    <Spin spinning={loading || loadingVerify} size="large">
      {isStaffPath && (
        <>
          {/* <Menu
            className={styles.menuStyle}
            onClick={onChangeTab}
            selectedKeys={[currentTab]}
            mode="horizontal">
            <Menu.Item key="staff" icon={<MedicineBoxOutlined />}>
              {translate('login.staff')}
            </Menu.Item>
          </Menu> */}
        </>
      )}
      <div className="px-7 md:px-7 lg:px-14">
        <h2 className="text-xl md:text-3xl font-bold text-secondary pb-7 mt-9">
          {translate('login.welcome')}
        </h2>
        <Form
          layout="vertical"
          requiredMark={false}
          onFinish={onFinish}
          onFinishFailed={onFinishFailed}
          onFieldsChange={onFinishFailed}
          validateMessages={validateMessages}
          initialValues={{
            remember: true
          }}>
          {/* {currentTab === 'staff' &&
            onChangeTabForm({
              labelUsername: 'login.username',
              itemNameUsername: 'userName',
              itemNamePassword: 'credentialsStaff',
              usernamePlaceholder: 'john@email.com'
            })} */}
          {currentTab === 'patient' &&
            onChangeTabForm({
              labelUsername: 'login.docid',
              itemNameUsername: 'docId',
              itemNamePassword: 'credentialsPatient',
              normalize: onKeepOnlyNumbers,
              usernamePlaceholder: '10567233'
            })}

          {isError && (
            <div className="mb-4 -mt-4">
              <Alert message={translate(messageError)} type="error" showIcon />
            </div>
          )}
          <div className="flex flex-row justify-end">
            {currentTab === 'patient' && (
              <Button
                onClick={() => history.push('/passwordRecovery')}
                type="link"
                className={`-mr-4 mb-3 ${!isError && '-mt-4'}`}>
                {translate('login.forgot')}
              </Button>
            )}
          </div>
          <Form.Item className="w-full">
            {currentTab === 'staff' ? (
              <Button
                className={styles.loginButton}
                type="primary"
                onClick={() => {
                  onLoginOkta();
                }}>
                {translate('login.loginStaff')}
              </Button>
            ) : (
              <Button
                className={styles.loginButton}
                type="primary"
                htmlType="submit">
                {translate('login.login')}
              </Button>
            )}
          </Form.Item>
          <div className="h-1" />
        </Form>
      </div>
    </Spin>
  );

  const mfaView = () => (
    <div className="px-7 md:px-7 lg:px-14">
      <Space align="baseline">
        <h2 className="text-xl md:text-3xl font-bold text-secondary pb-7 mt-9">
          <ArrowLeftOutlined onClick={() => onNavigationBack()} />
        </h2>
        <h2 className="text-xl md:text-3xl font-bold text-secondary ">
          {translate('login.mfaTitle')}
        </h2>
      </Space>
      <h2 className="font-bold text-base text-secundary text-left pb-4">
        {translate('login.mfaDetail')}
      </h2>
      <h2 className="text-base text-grayFontInput text-left">
        {translate('login.mfaFactorTitle')}
      </h2>
      <Menu
        className="w-full"
        onClick={onChangeTabFactor}
        selectedKeys={[currentFactorTab]}
        mode="horizontal">
        <Menu.Item
          key="email"
          style={{ fontSize: 16 }}
          icon={<MailFilled style={{ fontSize: 24 }} />}
          disabled={emailFactor.value === 'none'}>
          {translate('login.mfaEmailTitle')}
        </Menu.Item>
        <Menu.Item
          key="sms"
          style={{ fontSize: 16 }}
          icon={<MessageFilled style={{ fontSize: 24 }} />}
          disabled={smsFactor.value === 'none'}>
          {translate('login.mfaSmsTitle')}
        </Menu.Item>
        <Menu.Item
          key="call"
          style={{ fontSize: 16 }}
          icon={<PhoneFilled style={{ fontSize: 24 }} />}
          disabled={callFactor.value === 'none'}>
          {translate('login.mfaCallTitle')}
        </Menu.Item>
      </Menu>
      <Form
        className="pb-7 mt-9"
        layout="vertical"
        requiredMark={false}
        onFinish={onFinishMfa}
        onFinishFailed={onFinishFailed}
        onFieldsChange={onFinishFailed}
        validateMessages={validateMessages}
        initialValues={{
          remember: true
        }}>
        <div className="h-5" />
        {currentFactorTab === 'sms' && (
          <StyledMainDiv>
            {translate('login.mfaSmsMessage')}
            {smsFactor.value}
          </StyledMainDiv>
        )}
        {currentFactorTab === 'email' && (
          <StyledMainDiv>
            {translate('login.mfaEmailMessage')}
            {emailFactor.value}
          </StyledMainDiv>
        )}
        {currentFactorTab === 'call' && (
          <StyledMainDiv>
            {translate('login.mfaCallMessage')}
            {callFactor.value}
          </StyledMainDiv>
        )}
        <div className="h-5" />
        {isError && (
          <div className="mb-4 -mt-4">
            <Alert message={translate(messageError)} type="error" showIcon />
          </div>
        )}
        <Form.Item className="w-full">
          <Button
            className={styles.loginButton}
            type="primary"
            htmlType="submit">
            {translate('login.mfaSubmitButton')}
          </Button>
        </Form.Item>
        <div className="h-1" />
      </Form>
      <Divider />
      <div className="flex flex-row justify-left">
        {currentFactorTab === 'sms' && (
          <Button
            onClick={onUpdateInfo}
            type="link"
            className={`-mr-4 mb-3 ${!isError && '-mt-4'}`}>
            {translate('login.mfaSmsNotificationBar')}
          </Button>
        )}
        {currentFactorTab === 'email' && (
          <Button
            onClick={onUpdateInfo}
            type="link"
            className={`-mr-4 mb-3 ${!isError && '-mt-4'}`}>
            {translate('login.mfaEmailNotificationBar')}
          </Button>
        )}
        {currentFactorTab === 'call' && (
          <Button
            onClick={onUpdateInfo}
            type="link"
            className={`-mr-4 mb-3 ${!isError && '-mt-4'}`}>
            {translate('login.mfaCallNotificationBar')}
          </Button>
        )}
      </div>
    </div>
  );

  // Renderer callback with condition
  const renderer = ({ minutes, seconds }) => {
    // Render a countdown
    return (
      <span>
        {zeroPad(minutes)}:{zeroPad(seconds)}
      </span>
    );
  };

  const verifyView = () => {
    return (
      <div className="px-7 md:px-7 lg:px-14">
        <Spin spinning={loadingVerify} style={{ overflowY: 'inherit' }}>
          <Space align="baseline">
            <h2 className="text-xl md:text-3xl font-bold text-secondary">
              <ArrowLeftOutlined onClick={() => onNavigationBack()} />
            </h2>
            <h2 className="text-xl md:text-3xl font-bold text-secondary pb-2 mt-9">
              {translate('login.mfaTitle')}
            </h2>
          </Space>
          <h2 className="font-bold text-base text-secundary text-left">
            {translate('login.mfaEnterCode')}
            {currentFactorTab.toUpperCase()}
          </h2>
          <h2 className="text-base text-grayFontInput text-left">
            {translate('login.mfaCodeExpire')}
            <Countdown
              zeroPadTime={2}
              date={codeExpireRequestTime + codeExpireInMilliseconds}
              renderer={renderer}
              onComplete={() => setShowFaNewCodeAction(false)}
            />
            {translate('login.mfaCodeExpireMetric')}
          </h2>
          <Form
            layout="horizontal"
            requiredMark={false}
            onFinish={onFinishVerify}
            onFinishFailed={onFinishFailed}
            onFieldsChange={onFinishFailed}
            validateMessages={validateMessages}
            initialValues={{
              remember: true
            }}>
            {inputGroupSpaced()}
            {isError && (
              <div className="mb-4 -mt-4">
                <Alert
                  message={translate(messageError)}
                  type="error"
                  showIcon
                />
              </div>
            )}
            <Form.Item className="w-full">
              <Button
                className={styles.verifyButton}
                type="primary"
                htmlType="submit">
                {translate('login.mfaVerifyButton')}
              </Button>
            </Form.Item>
            <Form.Item>
              {showFaNewCodeAction && (
                <Button
                  onClick={() => onFinishMfa()}
                  type="link"
                  className={`-mr-4 mb-3 w-full ${!isError && '-mt-4'}`}>
                  {translate('login.mfaNewCode')}
                </Button>
              )}
            </Form.Item>
          </Form>
          <div className="h-10" />
          <Divider />
          <div className="flex flex-row justify-left">
            <Button
              onClick={() => onNavigationBack()}
              type="link"
              className={`-mr-4 mb-3 ${!isError && '-mt-4'}`}>
              {translate('login.mfaChangeFactor')}
            </Button>
          </div>
        </Spin>
      </div>
    );
  };

  const contactAdminView = () => (
    <div className="px-7 md:px-7 lg:px-14">
      <h2 className="text-xl md:text-3xl font-bold text-secondary pb-7 mt-9">
        <ArrowLeftOutlined onClick={() => onNavigationBack()} />
        {translate('login.mfaDataUpdate')}
      </h2>
      <h2 className="text-base font-bold text-grayFontInput text-center">
        {translate('login.mfaContantAdmin')}
      </h2>
      <Form
        layout="vertical"
        requiredMark={false}
        onFinish={() => onNavigationBack()}>
        <Form.Item className="w-full">
          <Button
            className={styles.loginButton}
            type="primary"
            htmlType="submit">
            {translate('login.mfaNavigateBack')}
          </Button>
        </Form.Item>
        <div className="h-1" />
      </Form>
    </div>
  );

  return (
    <div className={styles.loginViewStyle}>
      <div className="inline-flex items-center mb-7 md:hidden mt-20">
        <img src={baxterNephroLogo} className="mx-auto w-4/12 " />
        <img src={baxterRenalVertical} className="mx-auto mt-7 w-4/12" />
      </div>
      <div className="block w-3/4 hidden md:block">
        <img src={baxterNephroLogo} className="mx-auto w-3/4 md:w-1/2 " />
        <img
          src={baxterRenalHorizontal}
          className="mx-auto mt-7 w-3/4 md:w-1/2"
        />
        <div align="center" className={styles.cardVantive} id="vantiveNow">
          <div className={styles.vantiveMessage}>
            {translate('login.vantiveNow')}
          </div>
        </div>
      </div>
      <div className={styles.card}>
        {renderContent === 'auth' && authView()}
        {renderContent === 'mfa' && mfaView()}
        {renderContent === 'verify' && verifyView()}
        {renderContent === 'admin' && contactAdminView()}
      </div>
      <div
        className="inline-flex items-center mb-7 md:hidden mt-20"
        id="vantiveNow">
        <div align="center" className={styles.cardVantive}>
          <div className={styles.vantiveMessage}>
            {translate('login.vantiveNow')}
          </div>
        </div>
      </div>
    </div>
  );
}

/**
 * Component definition of proptypes.
 */
loginView.propTypes = {
  authActions: PropTypes.shape({
    logIn: PropTypes.func.isRequired,
    sendCode: PropTypes.func.isRequired,
    verifyCode: PropTypes.func.isRequired,
    getToken: PropTypes.func.isRequired
  }).isRequired,
  userActions: PropTypes.shape({
    setUserInfo: PropTypes.func.isRequired,
    getUserLoginInfo: PropTypes.func.isRequired
  }).isRequired,
  patientActions: PropTypes.shape({
    getIdentificationTypesArray: PropTypes.func.isRequired
  }).isRequired,
  loading: PropTypes.bool.isRequired
};

export default loginView;
